import axios from 'axios';
import { toast, ToastContainer } from 'react-toastify';
import React from 'react';
import { createRoot } from 'react-dom/client';
import store from '../state';
import { scheduleWarningsReceived } from '../state/modules/combined-schedules/actions';

export const handleError = err => {
  const scheduleWarnings = err.response?.data?.schedule_warnings || {};

  const receivedScheduleWarnings = Object.entries(scheduleWarnings).length > 0;

  // regardless of the origin, whenever there are schedule warnings, dispatch
  // them to the store. This is now done in 1 single place, and no operation has
  // to take this into account anymore. It happens automatically here.
  if (receivedScheduleWarnings) {
    store.dispatch(scheduleWarningsReceived(scheduleWarnings));
  }

  if (!receivedScheduleWarnings && err.response?.statusText) {
    renderToast();
    // Create the toast that will be shown in the dashboard
    toast.error(err.response?.statusText);
  }

  return Promise.reject(err);
};

let toastRoot = null;

const renderToast = () => {
  let element = document.querySelector('#react-toast');
  if (!element) {
    element = document.createElement('div');
    element.setAttribute('id', 'react-toast');
    document.body.appendChild(element);
  }

  const reactEl = <ToastContainer position="top-center" />;

  // use new React 18 createRoot API
  toastRoot = toastRoot || createRoot(element);
  toastRoot.render(reactEl);
};

const axiosRetryInterceptor = err => {
  let config = err.config;

  config.retry = config.retry || 3;

  // If config does not exist or the retry option is not set, reject
  if (!config || !config.retry) return handleError(err);

  const statusCode = err.response?.status || -1;
  // do not retry 4XX codes, except 429 (too many requests)
  if (statusCode != 429 && statusCode >= 400 && statusCode <= 499) {
    config.errorHandler && config.errorHandler(err);
    // Reject with the error
    return handleError(err);
  }

  // Set the variable for keeping track of the retry count
  config.__retryCount = config.__retryCount || 0;

  // Check if we've maxed out the total number of retries
  if (config.__retryCount >= config.retry) {
    config.errorHandler && config.errorHandler(err);
    // Reject with the error
    return handleError(err);
  }

  // Increase the retry count
  config.__retryCount += 1;

  const retryDelay = (config.retryDelay || 1000) * 1.5 ** config.__retryCount;

  // Create new promise to handle exponential backoff
  const backoff = new Promise(function (resolve) {
    setTimeout(function () {
      resolve();
    }, retryDelay);
  });

  // Return the promise in which recalls axios to retry the request
  return backoff.then(function () {
    return axios(config);
  });
};

let interceptorHandle = null;
export const setupAxiosRetryInterceptor = () => {
  if (interceptorHandle !== null) return;

  interceptorHandle = axios.interceptors.response.use(undefined, axiosRetryInterceptor);
};
